import type {
  PhaseTwoError,
  WorkspaceBootstrap,
  WorkspaceFolder,
  WorkspaceProject,
} from "@/components/workspace/workspace-types";
import { fetchJson } from "@/lib/api";
import { toast } from "sonner";
/**
 * Workspace data store.
 *
 * Stage 2 wires the store to `/api/workspace/bootstrap` and adds a phase-2
 * stub for every CRUD action (create/rename/move/delete folder or project).
 * Each stub fires a sonner toast so the UI exercises the real error path
 * before the D1-backed endpoints exist.
 *
 * The store is intentionally tiny — components should reach for
 * `useWorkspaceData()` selectors rather than re-reading the full snapshot.
 */
import { create } from "zustand";

export interface WorkspaceStoreState {
  folders: WorkspaceFolder[];
  projects: WorkspaceProject[];
  flags: WorkspaceBootstrap["flags"];
  loading: boolean;
  error: Error | null;
  /** Last successful fetch — used by the "Refresh notice" component. */
  lastFetchedAt: number | null;

  fetch: () => Promise<void>;
  reset: () => void;

  // Phase-2 stubs. Each returns a rejected promise so callers can await.
  createFolder: (input: { name: string; parent_id: string | null }) => Promise<never>;
  renameFolder: (input: { id: string; name: string }) => Promise<never>;
  moveFolder: (input: { id: string; parent_id: string | null }) => Promise<never>;
  deleteFolder: (input: { id: string }) => Promise<never>;
  createProject: (input: { name: string; folder_id: string | null }) => Promise<never>;
  renameProject: (input: { id: string; name: string }) => Promise<never>;
  moveProject: (input: { id: string; folder_id: string | null }) => Promise<never>;
  deleteProject: (input: { id: string }) => Promise<never>;
}

const EMPTY_FLAGS: WorkspaceBootstrap["flags"] = {
  folders_writable: false,
  projects_writable: false,
  import_enabled: false,
};

function phase2(feature: string, action: () => void): never {
  toast(`${feature} — coming in phase 2`, {
    description: "This action isn't wired up to D1 yet.",
    action: {
      label: "Dismiss",
      onClick: action,
    },
  });
  // Mirror the server's phase-2 payload for callers that want to inspect it.
  const err: PhaseTwoError & { __phase2: true } = {
    error: "phase_2",
    feature,
    __phase2: true,
  };
  throw err;
}

export const useWorkspaceStore = create<WorkspaceStoreState>((set) => ({
  folders: [],
  projects: [],
  flags: EMPTY_FLAGS,
  loading: false,
  error: null,
  lastFetchedAt: null,

  fetch: async () => {
    set({ loading: true, error: null });
    try {
      const payload = await fetchJson<WorkspaceBootstrap>("/workspace/bootstrap");
      set({
        folders: payload.folders,
        projects: payload.projects,
        flags: payload.flags,
        loading: false,
        lastFetchedAt: Date.now(),
      });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err : new Error(String(err)) });
      toast.error("Failed to load workspace", {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  },

  reset: () => {
    set({
      folders: [],
      projects: [],
      flags: EMPTY_FLAGS,
      loading: false,
      error: null,
      lastFetchedAt: null,
    });
  },

  createFolder: () => phase2("Create folder", () => {}),
  renameFolder: () => phase2("Rename folder", () => {}),
  moveFolder: () => phase2("Move folder", () => {}),
  deleteFolder: () => phase2("Delete folder", () => {}),
  createProject: () => phase2("Create project", () => {}),
  renameProject: () => phase2("Rename project", () => {}),
  moveProject: () => phase2("Move project", () => {}),
  deleteProject: () => phase2("Delete project", () => {}),
}));

/** Convenience hook — same selector API as `useWorkspaceStore` but typed for components. */
export function useWorkspaceData(): WorkspaceStoreState;
export function useWorkspaceData<T>(selector: (state: WorkspaceStoreState) => T): T;
export function useWorkspaceData<T>(
  selector?: (state: WorkspaceStoreState) => T
): WorkspaceStoreState | T {
  // Pass the selector through to Zustand when supplied; otherwise return the
  // full store snapshot. The two-callers pattern (`useWorkspaceData((s) => …)`)
  // matches Zustand's idiomatic API.
  if (selector) return useWorkspaceStore(selector);
  return useWorkspaceStore();
}

/** Returns a memoized selector with shallow equality, useful for derived UI. */
export function useProjectById(id: string | null | undefined): WorkspaceProject | undefined {
  return useWorkspaceStore((s) => (id ? s.projects.find((p) => p.id === id) : undefined));
}

export function useFolderById(id: string | null | undefined): WorkspaceFolder | undefined {
  return useWorkspaceStore((s) => (id ? s.folders.find((f) => f.id === id) : undefined));
}
